import { getFilledArrayOrDefault } from "../../utils/getFilledArrayOrDefault";
import type { OptionType } from "./SingleSelect";

/**
 * A multi-select value is an array in config, but layer and check meta track it as a comma separated string.
 */
export const normalizeMetaOptions = (meta_options?: string[] | string | null): string[] => {
    const fromString = typeof meta_options === "string" ? meta_options.split(",") : [];
    return getFilledArrayOrDefault(meta_options, fromString).map((i) => String(i).trim()).filter(Boolean);
};

/**
 * Matches the typed text against the option's label, value or one of its meta values (e.g. batch).
 */
export const filterOptionWithMeta = (
    option: { label: string; value: string; data: OptionType },
    input: string,
): boolean => {
    // * Always keep the "Create ..." entry of the creatable select
    if ((option.data as any)?.__isNew__) return true;
    const query = input.trim().toLowerCase();
    // * Match each field on its own so a query cannot span the label and a meta value
    return [option.label, option.value, ...(option.data?.meta ?? []).map((m) => m.value)]
        .some((text) => String(text).toLowerCase().includes(query));
};

/**
 * Turns child layers into dropdown options. Each option carries the child's values for the
 * configured meta keys (e.g. batch); keys the child has no value for are skipped.
 */
export const buildChildOptions = (
    children: Record<string, any>[],
    meta_options?: string[] | string | null,
): OptionType[] => {
    const metaKeys = normalizeMetaOptions(meta_options);
    return children.map((child) => ({
        label: child.label,
        value: child.label,
        meta: metaKeys
            .filter((key) => child[key] !== undefined && child[key] !== null && child[key] !== "")
            .map((key) => ({ key, value: String(child[key]) })),
    }));
};
